/**
 * What the outcome KPI's conversions figure may be CALLED — one answer, for
 * every surface that renders the campaign outcome card.
 *
 * The canonical `conversions` total is a platform-attributed count whose
 * real-world meaning depends entirely on what the configured trackers fire
 * on. For a campaign whose only trackers are site-wide page-view pixels,
 * every counted event is a visit to the campaign's destination site,
 * attributed by the delivery platform — click-through and view-through. For
 * other campaigns the same field counts ticket-page views, streaming-button
 * presses, checkouts or survey submissions.
 *
 * The backend verifies the LIVE tracker configuration and declares its ruling
 * as `outcomeSemantics` on the canonical payload:
 *
 *   - `ad_attributed_site_visits` — every tracker on every contributing
 *     campaign is a verified site-wide page-view pixel, and the StackAdapt
 *     rows alone account for exactly the displayed total.
 *   - `platform_reported_conversions` — everything else, including every
 *     uncertainty. Fail-closed: the generic name is always truthful; the
 *     specific one is an earned claim.
 *
 * Surfaces render the specific name ONLY under the declared classification and
 * keep "Conversions" otherwise. The declaration names the number; it never
 * changes it — value, formatting, freshness and source basis are governed
 * elsewhere and are identical in both classifications.
 *
 * THE NAME IS CAMPAIGN-SCOPED. The metric is a property of the campaign — how
 * many site visits its paid media produced — and it is built to GAIN
 * contributors: YouTube and TikTok join the same figure the moment their
 * attribution is wired. A vendor name in the title would freeze one supplier
 * into a product-level metric and would have to be renamed each time a
 * contributor is added, so provider identity lives underneath, on the
 * decomposition and in the methodology surface, where provenance belongs.
 *
 * What the figure does not YET cover is a real limit and is disclosed from
 * evidence rather than assumed away: the backend derives a coverage sentence
 * from the channels that actually reported against the channels that actually
 * delivered ("Current attributed contributors: CTV + Display. YouTube
 * attribution is not yet included."). A typed sentence would be correct until
 * the day it silently was not — which is exactly how a design fixture went on
 * naming a retired label after a rename, in production.
 *
 * The audit's three findings still hold and still travel with the number: these
 * are attributed EVENTS rather than unique visitors, coverage is partial, and
 * view-through attribution competes with other platforms' models. They are in
 * the definition below, not in the title.
 *
 * This is deliberately NOT the Site Visits · GA4 overlay. The overlay is an
 * observation of ALL site traffic regardless of source; this KPI counts only
 * visits the delivery platform attributed to paid delivery. The two live on
 * the same page, are never added together, and must never be conflated — which
 * is why the definition sentence below states the distinction explicitly.
 *
 * Byte-identical in the Cortex client and the Quantum superadmin: two portals
 * that disagree about when a number may carry the specific name would show a
 * governed claim in one place and a generic label in the other, from the same
 * payload.
 */

export const AD_ATTRIBUTED_SITE_VISITS_CLASSIFICATION = "ad_attributed_site_visits";
export const PLATFORM_REPORTED_CONVERSIONS_CLASSIFICATION = "platform_reported_conversions";

export const AD_ATTRIBUTED_SITE_VISITS_LABEL = "Ad-Attributed Site Visits";
export const PLATFORM_REPORTED_CONVERSIONS_LABEL = "Conversions";

/*
 * What the number is, what it includes, and what it is not — in that order,
 * because a reader who stops after the first clause should still not have been
 * misled. The view-through clause is not a hedge: view-through attribution and
 * an analytics property's last-click acquisition are different measurement
 * models that routinely claim the same page view, and a figure that mixes
 * click-through with view-through owes the reader that fact.
 */
export const AD_ATTRIBUTED_SITE_VISITS_DEFINITION =
  "Site visits attributed to this campaign's paid media, including eligible click-through and view-through attribution. View-through attribution may overlap with other platforms' attribution models. Counts attributed events rather than unique visitors, and covers only the platforms whose attribution is currently wired. Distinct from Site Visits · GA4, which observes all site traffic regardless of source.";

function looksLikeOutcomeSemantics(candidate) {
  return Boolean(
    candidate &&
      typeof candidate === "object" &&
      typeof candidate.classification === "string" &&
      typeof candidate.metricKey === "string"
  );
}

/**
 * The outcome-semantics declaration carried by `source`, or null.
 *
 * The place-list mirrors the observed-site-visits resolver, and for the same
 * reason: the reporting spine serves one declaration through three envelopes
 * (public payload root's `canonicalReporting`, the authenticated
 * `canonicalReporting`/`canonicalSummary` pair, and an unwrapped summary),
 * and a surface that knows only one envelope silently reads "no declaration"
 * from the others. Null is the truthful answer for "this payload declares
 * nothing", and every consumer must treat it exactly like the generic
 * classification.
 */
export function resolveOutcomeSemantics(source) {
  if (!source || typeof source !== "object") return null;

  const candidate =
    (looksLikeOutcomeSemantics(source.outcomeSemantics) && source.outcomeSemantics) ||
    (looksLikeOutcomeSemantics(source?.canonicalReporting?.outcomeSemantics) &&
      source.canonicalReporting.outcomeSemantics) ||
    (looksLikeOutcomeSemantics(source?.canonicalSummary?.outcomeSemantics) &&
      source.canonicalSummary.outcomeSemantics) ||
    /* Already the declaration — model layers hand it over bare. */
    (looksLikeOutcomeSemantics(source) && source) ||
    null;
  if (!candidate) return null;

  return {
    metricKey: candidate.metricKey,
    classification: candidate.classification,
    basis: typeof candidate.basis === "string" ? candidate.basis : null,
  };
}

/**
 * True only for a declaration that governs THIS metric and earns the specific
 * name. Any other metric key, any other classification, and any absent or
 * malformed declaration answer false — the generic label is the default, not
 * a fallback that needs claiming.
 */
export function outcomeSemanticsDeclareSiteVisits(semantics) {
  return Boolean(
    semantics &&
      semantics.metricKey === "conversions" &&
      semantics.classification === AD_ATTRIBUTED_SITE_VISITS_CLASSIFICATION
  );
}

/**
 * The presentation ruling every outcome-card builder consumes: label,
 * long-form label and definition copy for the conversions figure carried by
 * `source`. Exactly two answers exist, and the generic one carries no
 * definition — the generic card has never asserted more than its label says.
 */
export function resolveOutcomeConversionsPresentation(source) {
  if (outcomeSemanticsDeclareSiteVisits(resolveOutcomeSemantics(source))) {
    return {
      classification: AD_ATTRIBUTED_SITE_VISITS_CLASSIFICATION,
      label: AD_ATTRIBUTED_SITE_VISITS_LABEL,
      longLabel: AD_ATTRIBUTED_SITE_VISITS_LABEL,
      definition: AD_ATTRIBUTED_SITE_VISITS_DEFINITION,
    };
  }
  return {
    classification: PLATFORM_REPORTED_CONVERSIONS_CLASSIFICATION,
    label: PLATFORM_REPORTED_CONVERSIONS_LABEL,
    longLabel: PLATFORM_REPORTED_CONVERSIONS_LABEL,
    definition: null,
  };
}
